<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useDisplay } from 'vuetify'
import { getNotificationVisual } from '../composables/useNotificationType'
import { useNotificationStore } from '../stores/notificationStore'
import { handleNotificationUrl } from '../utils/notificationNavigation'
import type { INotification } from '../types/notification.types'
import NotificationActions from './NotificationActions.vue'
import NotificationContent from './NotificationContent.vue'
import { useReactiveNow } from '@/utils/useReactiveNow'
import { formatRelativeDay, formatTimeOnly } from '@/utils/formatters'

/**
 * Клик по карточке (не по кнопкам/чекбоксу/стрелке) помечает уведомление прочитанным.
 * Стрелка-перехода активна только если у уведомления заполнено поле url.
 * Клик по стрелке идёт через handleNotificationUrl (глобальные формы), иначе — переход по ссылке.
 * Признак непрочитанности визуально передаётся жирным шрифтом title.
 */
const props = defineProps<{
  notification: INotification
}>()

const store = useNotificationStore()
const { selectedIds, starredIds } = storeToRefs(store)
const { mdAndUp } = useDisplay()

const now = useReactiveNow()
const visual = computed(() => getNotificationVisual(props.notification.messageType))

const isSelected = computed(() => selectedIds.value.has(props.notification.id))
const isStarred = computed(() => starredIds.value.has(props.notification.id))

const hasButtons = computed(() => props.notification.buttons.length > 0)
const hasNav = computed(() => Boolean(props.notification.urlProps))

const dayLabel = computed(() => formatRelativeDay(props.notification.createdAt, now.value))
const timeLabel = computed(() => formatTimeOnly(props.notification.createdAt))

/* VAvatar variant="tonal" в нашей теме рендерится без backgroundColor — задаём вручную. */
const avatarStyle = computed(() => ({
  backgroundColor: `rgba(var(--v-theme-${visual.value.color}), 0.2)`,
}))

function onSelectToggle(): void {
  store.toggleSelected(props.notification.id)
}

function onStarToggle(): void {
  store.toggleStarred(props.notification.id)
}

function onCardClick(): void {
  if (!props.notification.isSeen) {
    store.markOneAsRead(props.notification.id)
  }
}

async function onArrowClick(): Promise<void> {
  if (!props.notification.urlProps) return
  if (!props.notification.isSeen) {
    await store.markOneAsRead(props.notification.id)
  }
  handleNotificationUrl(props.notification)
}
</script>

<template>
  <article
    class="notif-center-item d-flex align-center"
    :class="{ 'notif-center-item--unread': !notification.isSeen, 'notif-center-item--selected': isSelected }"
    @click="onCardClick"
  >
    <!-- Чекбокс выделения -->
    <div class="notif-center-item__col-checkbox" @click.stop>
      <VCheckbox
        :model-value="isSelected"
        hide-details
        density="compact"
        @update:model-value="onSelectToggle"
      />
    </div>

    <!-- Аватар с цветной подложкой по типу -->
    <VAvatar
      :color="visual.color"
      variant="tonal"
      size="40"
      :style="avatarStyle"
    >
      <VIcon :icon="visual.icon" size="24" />
    </VAvatar>

    <!-- Текст: title + message. На mobile под текстом — action-кнопки. -->
    <div class="notif-center-item__content min-w-0">
      <NotificationContent
        v-if="notification.title"
        class="notif-center-item__title"
        :text="notification.title"
        format="default"
      />
      <NotificationContent
        v-if="notification.message"
        class="notif-center-item__message"
        :text="notification.message"
        :format="notification.format"
      />
      <NotificationActions
        v-if="hasButtons && !mdAndUp"
        :buttons="notification.buttons"
      />
    </div>

    <!-- На desktop action-кнопки — отдельная колонка справа от текста. -->
    <NotificationActions
      v-if="hasButtons && mdAndUp"
      :buttons="notification.buttons"
    />

    <!-- Дата: верхняя строка — день, нижняя — точное время -->
    <div class="notif-center-item__col-date d-flex flex-column align-end">
      <span class="notif-center-item__day">{{ dayLabel }}</span>
      <span class="notif-center-item__time">{{ timeLabel }}</span>
    </div>

    <IconBtn
      v-if="hasNav"
      size="small"
      variant="text"
      class="notif-center-item__arrow"
      aria-label="Перейти к связанной сущности"
      @click.stop="onArrowClick"
    >
      <VIcon icon="ri-arrow-right-s-line" />
    </IconBtn>
  </article>
</template>

<style scoped lang="scss">
.notif-center-item {
  align-items: center;
  gap: 12px;
  padding: 16px;
  flex-wrap: wrap;
  border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.03);
  }

  &--unread {
    background: rgba(var(--v-theme-primary), 0.04);
  }

  &--selected {
    background: rgba(var(--v-theme-primary), 0.08);
  }

  &--unread .notif-center-item__title :deep(.notification-content) {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.95);
  }

  &__col-checkbox {
    flex-shrink: 0;
    inline-size: 36px;
  }

  &__col-star {
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    gap: 4px;
    min-inline-size: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    color: rgba(var(--v-theme-on-surface), 0.9);
  }

  &__message {
    font-size: 13px;
    line-height: 18px;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  &__col-date {
    flex-shrink: 0;
    min-inline-size: 90px;
  }

  &__col-date {
    gap: 4px;
  }

  &__day,
  &__time {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.4);
    white-space: nowrap;
  }

  &__day { line-height: 18px; }
  &__time { line-height: 20px; }

  &__arrow {
    flex-shrink: 0;
    inline-size: 34px;
    block-size: 34px;
    background: rgba(138, 141, 147, 0.16);
    border-radius: 6px;

    &:hover {
      background: rgba(138, 141, 147, 0.24);
    }
  }
}

@media (max-width: 1279px) {
  .notif-center-item__content {
    flex: 1 1 100%;
    order: 10;
  }
}
</style>
